import { Alert, InputAdornment, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ATLunchBenefitOptionsDTOPlaceEnum,
  BenefitActivationDTO,
  BenefitDTOBenefitEnum,
  BenefitTemplateItemDTO,
  CreateBenefitActivationDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { RadioControl } from 'probonio-shared-ui/component/form';
import { useTenant } from 'probonio-shared-ui/module/me';
import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextFieldControl } from '../../../../component/form';
import { BenefitActivationDialogExtensionInterface } from '../../../../extension/interface/benefit-activation-dialog.extension.interface';
import { ActivationDialogExtensions } from '../../ActivationDialog';

interface ATLunchBenefitOptions {
  dailyLimit: number;
  place: ATLunchBenefitOptionsDTOPlaceEnum;
}

const Fields: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { control, watch, setValue } = useFormContext();
  const { tenant } = useTenant();

  const place = watch('benefitOptions.place') as ATLunchBenefitOptionsDTOPlaceEnum;

  const limits: Record<ATLunchBenefitOptionsDTOPlaceEnum, number> = useMemo(() => {
    return {
      [ATLunchBenefitOptionsDTOPlaceEnum.Supermarket]: (tenant?.taxConfig?.atLunchSupermarketDailyLimit ?? 0) / 100,
      [ATLunchBenefitOptionsDTOPlaceEnum.Restaurant]: (tenant?.taxConfig?.atLunchRestaurantDailyLimit ?? 0) / 100,
    };
  }, [tenant?.taxConfig?.atLunchRestaurantDailyLimit, tenant?.taxConfig?.atLunchSupermarketDailyLimit]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'benefitOptions.place') {
        const place = value.benefitOptions?.place as ATLunchBenefitOptionsDTOPlaceEnum;

        const defaultLimit = limits[place];
        const currentLimit = value.benefitOptions?.dailyLimit;
        if (currentLimit > defaultLimit) {
          setValue('benefitOptions.dailyLimit', defaultLimit);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, limits]);
  // TODOFLO beim SWITCH von 2€ zurück springt der Betrag nicht auf 8€
  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginBottom: 1,
        marginTop: 2,
      }}
    >
      <Grid size={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('AT_LUNCH.changePlaceInfo')}
        </Alert>
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: 'medium',
          }}
        >
          {t('AT_LUNCH.fields.place.title')}
        </Typography>
        <RadioControl
          control={control}
          name="benefitOptions.place"
          options={[
            {
              label: t('AT_LUNCH.fields.place.RESTAURANT', { money: tenant?.taxConfig?.atLunchRestaurantDailyLimit }),
              value: ATLunchBenefitOptionsDTOPlaceEnum.Restaurant,
            },
            {
              label: t('AT_LUNCH.fields.place.SUPERMARKET', { money: tenant?.taxConfig?.atLunchSupermarketDailyLimit }),
              value: ATLunchBenefitOptionsDTOPlaceEnum.Supermarket,
            },
          ]}
        />
      </Grid>
      <Grid size={12}>
        <TextFieldControl
          slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> }, htmlInput: { step: 0.01 } }}
          rules={{
            required: true,
            min: 0.01,
            max: limits[place] && {
              value: limits[place],
              message: t('AT_LUNCH.maxDailyLimitError', { value: limits[place] * 100 }),
            },
          }}
          type="number"
          control={control}
          variant="outlined"
          name="benefitOptions.dailyLimit"
          label={t('AT_LUNCH.fields.dailyLimit')}
          margin="dense"
          fullWidth
        />
      </Grid>
    </Grid>
  );
};

const mapATLunchOptions = (benefitOptions: ATLunchBenefitOptions) => ({
  dailyLimit: Math.round(benefitOptions.dailyLimit * 100),
  place: benefitOptions.place,
});

const createATLunchBenefitActivation: ActivationDialogExtensions<ATLunchBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.AtLunch,
    atLunchOptions: mapATLunchOptions(benefitOptions),
  };
  await saveActivation(create);
};

const updateATLunchBenefitActivation: ActivationDialogExtensions<ATLunchBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    atLunchOptions: mapATLunchOptions(benefitOptions),
  };
  await saveActivation(update);
};

const resetATLunchBenefitOptions = (selectedActivation?: BenefitActivationDTO | BenefitTemplateItemDTO): ATLunchBenefitOptions => {
  return {
    dailyLimit: selectedActivation?.atLunchOptions ? selectedActivation.atLunchOptions.dailyLimit / 100 : 8,
    place: selectedActivation?.atLunchOptions?.place || ATLunchBenefitOptionsDTOPlaceEnum.Restaurant,
  };
};

export class ATLunchBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<ATLunchBenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);

  onCreateActivation = createATLunchBenefitActivation;
  onUpdateActivation = updateATLunchBenefitActivation;
  onResetBenefitOptions? = resetATLunchBenefitOptions;
}
